import {
  Award,
  Home,
  Users,
  LifeBuoy,
  Receipt,
  Inbox,
  Settings,
  UserCog,
  MessageCircleHeart,
  HelpCircle,
  FilePenLine,
  Vote,
  HandCoins,
  FolderOpen,
  Ticket,
  CreditCard,
  Hospital,
  Gavel,
  FileSpreadsheet,
} from 'lucide-react';
import type { NavItemDef, NavSection } from './sidebar-primitives';
import { NAV_KEY, navGroup, navItem } from '@/i18n/keys/nav';

/**
 * The TRFer nav, as data.
 *
 * Extracted from `TRFerSidebar` (#611). It lived inside the component, which
 * meant `HelpPage` kept a hand-written second copy of the same list — and when
 * #769 PR2 restructured the groups wholesale, Help was left describing a nav
 * that no longer existed: it documented a "Circles" entry renamed in F-032 and
 * omitted fourteen live surfaces. That is not a copy someone forgot to update;
 * it is a copy that cannot be kept updated.
 *
 * So the list lives here, the sidebar renders it, the help page derives its
 * cards from it, and `HelpPage.navParity.test.tsx` fails the build if a nav
 * item has no help entry. A new surface now ships with its documentation or it
 * does not ship.
 */

export interface TrferNavPermissions {
  /** `user.permissions.can_assign_trfers` — the lead gate (#455). */
  canAssignTrfers: boolean;
  /**
   * `user.permissions.can_review_applications`.
   *
   * Gates Committee (2026-10-26): the page's list and every write need it,
   * and its route is guarded on it.
   */
  canReviewApplications: boolean;
  /**
   * Show Unreads (`/trfer/threads`). False while the live conversation is
   * dormant (#2566, `useLiveConversation`).
   * Defaults to true so the help page and the parent map keep the full nav.
   */
  showUnreads?: boolean;
}

/**
 * Footer utilities, rendered outside the grouped nav by the sidebar itself.
 *
 * Exported so the parity test sees the complete surface list. Settings earns a
 * help card; Help does not — a help entry describing the help page is a
 * self-reference, and the parity test exempts it by href.
 */
export const TRFER_FOOTER_ITEMS: NavItemDef[] = [
  navItem(NAV_KEY.settings, '/trfer/settings', Settings),
  navItem(NAV_KEY.help,     '/help',           HelpCircle),
];

/** The one href in the nav that is deliberately not documented on the help page. */
export const HELP_SELF_HREF = '/help';

// Job-grouped nav (#624, frontend-clarity-guideline §3): group names answer
// "what am I here to do", no group over 7 items. Labels are sentence case per
// the design grammar; "TRF Applications" keeps its ratified Title Case as a
// named program surface. ("Grant Requests" left the lock on 2026-10-25: the
// row is "Requests".)
//
// A short history, most recent last:
// - 2026-08-14 (F-10): Today / Caseload / Money / Insight became GRANT WORK ·
//   PRESENCE · MANAGE — the order encodes what TRF does, gating stays per item.
// - 2026-09-13 (#1957, #1959, DECISIONS 2026-09-13): GRANT WORK split into
//   TRF Applications · Grants · Grant tracking; PRESENCE became Community.
// - 2026-09-14 (DECISIONS 2026-09-14, three entries): Grant tracking split
//   into Awards (Grants) and Balances (Grant spending); Family queue, Family
//   reports and Calendar folded into Families and Schedule; Handoff config
//   and the Ledger became Settings tabs, so Configure and Insight retired;
//   Manage became one Users page.
// - 2026-09-17 (staff-lists wave): the Fundraising preview retired and the
//   one-row Exploring group with it.
//
// 2026-09-18 — subtraction in the chrome (DECISIONS "Subtraction in the
// chrome", ui-ux-guideline §2.8). A nav row is one noun: Threads · Live →
// Unreads, Conversations → Threads, Support & Resources → Support, Spend &
// Expense → Spend. Unreads is the inbox, so it sits where an inbox sits —
// ungrouped, directly under Today. Families and Schedule are the TRFer's
// administration and sit in Manage (Families · Schedule · Users; Users alone
// keeps the lead gate). Support is content TRF publishes to parents and sits
// in Community directly after TRF Announcements (renamed 2026-10-23, see
// the Community group below). Caseload is a retired group
// name, like Insight and Configure. Every href and every menu key is
// unchanged — labels rename, keys never.
const ungroupedSection: NavSection = {
  // Ungrouped top: Today, then the inbox. The five per-family surfaces that
  // sat here until 2026-09-13 moved to a group at the bottom (Victor: "move
  // these items to a different section — at the bottom of the sidebar"), so
  // the first group a TRFer reads is the grant application lane.
  items: [
    // "Today", not "Home" — ratified DECISIONS 2026-08-26, design grammar
    // §4.4. Href and the `dashboard` menu key unchanged.
    navItem(NAV_KEY.today, '/trfer', Home, { end: true }),
    // Unreads (2026-09-18, Q2 — the Slack position): the handoffs waiting for
    // a person, named for what the reader does there, not for the mechanism
    // ("Threads · Live" until 2026-09-18). The count on this row and the
    // app-bar bell read the same endpoint (§2.7). Href and the `threads`
    // menu key unchanged.
    navItem(NAV_KEY.unreads, '/trfer/threads', Inbox),
  ],
};

// Was "Presence" (F-10, 2026-08-14). Renamed 2026-09-13 (#1959, DECISIONS
// 2026-09-13): occasional work a TRFer chooses, listed after every grant
// group. "Presence" survives as the service-posture concept in the PRD
// glossary, not as a nav label. Read TRF Announcements · Support · Polls ·
// Threads from 2026-09-18 (§2.8 rule 3). Since 2026-10-23 (DECISIONS
// "Community rows renamed; Threads removed") it reads Announcements ·
// Resources · Check-ins & polls: each row names what the page holds, and
// Threads is gone — it graded the companion's escalations, and TRF is not a
// crisis line (v1.141.0), so there was nothing left to grade. AI review is
// admin work on /admin/reviews.
const communitySection: NavSection = navGroup(NAV_KEY.groupCommunity, [
    // Same key and label as the parent nav's row: "TRF Announcements" from
    // 2026-09-12, "Announcements" since 2026-10-23 (the row sits in Community,
    // so "TRF" added nothing). Eyebrow follows (grammar §4.4); href and menu
    // key `community` unchanged.
    navItem(NAV_KEY.announcements, '/trfer/community',     MessageCircleHeart),
    // The library of outside help TRF points families to. "Support &
    // Resources" until 2026-09-18, "Support" until 2026-10-23: "Support"
    // read as a service TRF provides. Its own key since then — the parent
    // row (`NAV_KEY.support`) reads "Resources" too since wave w1023c. Href and the
    // `resources` menu key unchanged.
    navItem(NAV_KEY.resources,     '/trfer/resources',     LifeBuoy),
    // "Polls" until 2026-10-23: a check-in sent from Forms is a poll in
    // storage and is listed here, so the row names both. Href and the
    // `polls` menu key unchanged.
    navItem(NAV_KEY.polls,         '/trfer/polls',         Vote),
]);

// There is no Insight group since 2026-09-14 (#1979). Its one row, the
// Ledger, was removed on 2026-10-09 (#2452).

// There is no Configure group since 2026-09-14. Handoff config — its one row
// since Forms and Family wording left — is the Handoff tab of Settings
// (`/trfer/settings?tab=handoff`; `/trfer/handoff-config` redirects there).
// The 2026-08-29 Family wording ruling still holds: every slot in the copy
// registry is `apply.*`, so the editor is the second view of Forms and
// `/trfer/copy` redirects into it carrying `?slot=`. `copySlots.verify.test.ts`
// fails the moment a slot outside `apply.*` is registered, which is the signal
// to give wording a row back — in TRF Applications, beside Forms.

/**
 * Build the nav for one reader.
 *
 * Gating is PER ITEM, never per group: a non-lead sees a shorter list, never
 * an empty section and never a link that bounces (F-10). Sections whose items
 * were all gated away are dropped so an empty label cannot render.
 */
export function buildTrferNavSections({
  canAssignTrfers,
  canReviewApplications,
  showUnreads = true,
}: TrferNavPermissions): NavSection[] {
  // The application lane, in the order the work happens (#1957; renamed and
  // re-ordered 2026-09-13 at Victor's direction: "Applications → Grant
  // Applications; Pipeline → Queue; Referrals → Referral codes"). Eligibility
  // (#1013) and Pipeline (#1022) were one- and two-item groups only because
  // Grant work sat at the 7-item pin; splitting the lane out is what dissolved
  // them. Every gate is exactly what it was: the applications, pipeline
  // and offers routers are all guarded on `can_review_applications`. The
  // Pre-screen appeals row left on 2026-10-26 with the appeals process (#3307).
  //
  // "TRF Applications" — Victor's name for the group (2026-09-13, fourth
  // pass; was "Grant Applications" for one day). An application is a family
  // asking to JOIN TRF; a grant request is an admitted family asking for a
  // grant (the Grants group). "Grant Applications" read as the second thing.
  // Title Case by Victor's instruction — the sentence-case rule yields to a
  // named program surface.
  const applicationsSection: NavSection = navGroup(NAV_KEY.groupTrfApplications, [
      // Applications: the one review surface (2026-09-13, third pass). The
      // per-stage counts that were "Queue" (/trfer/pipeline) are a strip above
      // its toolbar, and the outstanding offers that were "Offers"
      // (/trfer/offers) are the same list filtered to `offer_sent` — both
      // were views over the same records, and both hrefs now redirect here.
      //
      // UNGATED since 2026-09-22 (Victor: "a plain TRFer should be able to
      // read the TRF-wide application"). The row is safe to show because the
      // page no longer 403s a reader without `can_review_applications` — the
      // backend mount opened its reads the same day, and the page renders
      // read-only for that reader. A row is only worth hiding when it leads
      // somewhere that would bounce; this one no longer does.
      navItem(NAV_KEY.applications, '/trfer/applications', FolderOpen),
      // Committee and Import families (2026-10-26, Victor): off the
      // Applications toolbar, which now only finds and sorts rows. Committee
      // is the package page (each family's page, the deck, all answers);
      // `/trfer/committee-package` keeps its href. Import families brings
      // renewing families in from a spreadsheet; the import's own gate is
      // admins and leads (`canImportFamilies`), which `canAssignTrfers` is.
      ...(canReviewApplications ? [navItem(NAV_KEY.committee, '/trfer/committee-package', Gavel)] : []),
      ...(canAssignTrfers ? [navItem(NAV_KEY.importFamilies, '/trfer/family-imports', FileSpreadsheet)] : []),
      // Forms: the instrument the lane runs on — the application, the
      // pre-screening, the check-ins. Moved here from Manage 2026-09-13; the
      // 2026-08-29 ruling that took it out of Configure (managed work, not
      // developer setup) holds. Ungated on purpose: every TRFer edits every
      // form at full capability (the recorded policy the backend enforces on
      // all /api/forms staff routes).
      navItem(NAV_KEY.forms, '/trfer/forms', FilePenLine),
      // Referral codes: everything that OPENS an application — the one-time
      // codes sent as links, the reusable partner codes, the cycles they mint
      // into. Held the F-18 placeholder until 2026-09-13; the `referrals`
      // menu key is unchanged. Ungated: the routes are staff-baseline.
      navItem(NAV_KEY.referralCodes, '/trfer/referrals', Ticket),
      // Partner hospitals: the list the /apply referral step's typeahead
      // picks from. Beside Referral codes because both are what OPENS an
      // application, and ungated for the same reason — the routes are
      // staff-baseline. Was Admin → Partner hospitals until 2026-09-20.
      navItem(NAV_KEY.partnerHospitals, '/trfer/hospitals', Hospital),
  ]);

  // GRANTS — one group since 2026-09-19 (Victor: "consolidate Grant
  // Requests, awards, reimbursements and spend under grants"). The rows run
  // in the order a grant moves: a request is decided (Requests), the
  // approval becomes an award with its payments (Awards), and money leaves
  // the award through a family's receipt (Reimbursements) or a card
  // (Card charges). The 2026-09-14 split into Grants / Grant spending
  // retired with it. Requests alone keeps the lead gate; a plain
  // TRFer sees three rows. Both queues are shared across all staff (#584 /
  // DECISIONS 2026-08-10 D-4) and each approval draws down a named grant.
  const grantsSection: NavSection = navGroup(NAV_KEY.groupGrants, [
      ...(canAssignTrfers
        // "Requests" since DECISIONS 2026-10-25; the key `grantRequests`,
        // the route and the menu key `grants` never rename.
        ? [navItem(NAV_KEY.grantRequests, '/trfer/grants', HandCoins)]
        : []),
      // One row per award since 2026-09-19 (it absorbed Balances —
      // `/trfer/balances` and `/trfer/grant-totals` redirect here; the
      // `grant-totals` menu key still governs it, keys never rename).
      navItem(NAV_KEY.awards,         '/trfer/awards',         Award),
      // NOT a caseload surface — the reimbursement queue is shared across
      // all staff. The page says so on screen.
      // "(demo)" since 2026-09-19 (grant lists redesign, R10): the pages are
      // simulated end to end and the sidebar says so; the in-page banner
      // explains. Routes and menu keys unchanged.
      navItem(NAV_KEY.reimbursements, '/trfer/reimbursements', Receipt),
      // "Spend & Expense" until 2026-09-18, "Spend" until 2026-09-19: the
      // row names what the page holds — BILL card charges waiting for a
      // decision. Route and `spend` menu key unchanged. `(?)` card-charges-label.
      navItem(NAV_KEY.cardCharges,    '/trfer/spend',          CreditCard),
  ]);

  // MANAGE — the TRFer's administration (2026-09-18, Q1, §2.8 rule 3):
  // Families · Schedule · Users, most-used first. Families and Schedule were
  // the Caseload group until 2026-09-18; Caseload is a retired group name.
  // Users alone keeps the lead gate (#455: the backend mounts and route
  // guards require can_assign_trfers), and gating stays per item (F-10), so a
  // plain TRFer sees Manage with two rows, never an empty label and never a
  // link that bounces.
  //
  // Users is one page since 2026-09-14 (#1980, Victor: "1 link, 1 page to do
  // administrative work"): Pending signup is a status filter on the roster
  // with Approve / Reject as row actions. Assignments moved to Families in
  // wave w1024 (2026-10-24): `?view=assignments` and `/trfer/families/manage`
  // redirect there. `/trfer/signups` redirects in; the `signups` and
  // `families-manage` menu keys are unmapped — `users` governs the page.
  const manageSection: NavSection = navGroup(NAV_KEY.groupManage, [
      navItem(NAV_KEY.families, '/trfer/families', Users),
      // Schedule (#1084) left on 2026-09-19 (desk pages redesign, S1): its
      // rows were parents' own to-dos, which staff have no verb for.
      // `/trfer/schedule` redirects to Families.
      ...(canAssignTrfers
        ? [navItem(NAV_KEY.users, '/trfer/users', UserCog)]
        : []),
  ]);

  // Order encodes the tiering (PRD 2026-09-13 §7): the inbox with Today, the
  // grant lanes, then administration, then the occasional community
  // surfaces. Configure and Insight went on 2026-09-14 (Handoff config and
  // the Ledger are Settings tabs); Exploring went on 2026-09-17; Caseload
  // went on 2026-09-18 (its rows are Manage's).
  // Unreads hides while the live conversation is dormant (#2566); Today stays.
  const topSection: NavSection = showUnreads
    ? ungroupedSection
    : { ...ungroupedSection, items: ungroupedSection.items.filter((i) => i.href !== '/trfer/threads') };

  return [
    topSection,
    applicationsSection,
    grantsSection,
    manageSection,
    communitySection,
  ].filter((s) => s.items.length > 0);
}

/** Flatten a built nav to its items — the shape both the help page and the parity test want. */
export function flattenTrferNav(sections: NavSection[]): NavItemDef[] {
  return sections.flatMap((s) => s.items);
}

// A `TRFER_CANONICAL_PARENTS` map lived here (batch 11, design grammar §4.2).
// `lib/parentFor.ts`'s `parentFor` derives every route's canonical parent by
// longest-prefix match over this module's own nav data, so it was deleted
// (#1475) rather than wired in.
